import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CreditCard,
  ShieldAlert,
  ShieldCheck,
  Cog,
  KeyRound,
  AlertTriangle,
  InfoIcon,
} from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { Helmet } from 'react-helmet';

interface MetaConversionConfig {
  pixel_id: string;
  access_token: string;
  test_event_code?: string;
  lead_event_source: string;
  api_version: string;
}

export function MetaConversionsSettings() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<MetaConversionConfig>({
    pixel_id: '',
    access_token: '',
    test_event_code: '',
    lead_event_source: 'Solar CRM',
    api_version: 'v17.0',
  });
  const [isTesting, setIsTesting] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [testPhone, setTestPhone] = useState('');

  // Get current configuration
  const {
    data: config,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['/api/meta-conversions/config'],
    retry: false,
  });

  // Initialize form with existing configuration
  useEffect(() => {
    if (config) {
      setFormData({
        pixel_id: config.pixel_id || '',
        access_token: config.access_token ? '••••••••' : '',
        test_event_code: config.test_event_code || '',
        lead_event_source: config.lead_event_source || 'Solar CRM',
        api_version: config.api_version || 'v17.0',
      });
    }
  }, [config]);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Save configuration mutation
  const saveConfig = useMutation({
    mutationFn: async (data: MetaConversionConfig) => {
      // Don't send masked access token back to server
      const payload = { ...data };
      if (payload.access_token === '••••••••') {
        delete payload.access_token;
      }
      return apiRequest({
        url: '/api/meta-conversions/config',
        method: 'POST',
        data: payload,
      });
    },
    onSuccess: () => {
      toast({
        title: 'Settings Saved',
        description: 'Meta Conversions API configuration has been updated.',
        variant: 'default',
      });
      queryClient.invalidateQueries({ queryKey: ['/api/meta-conversions/config'] });
    },
    onError: (error) => {
      toast({
        title: 'Error Saving Settings',
        description: `Failed to save configuration: ${error.toString()}`,
        variant: 'destructive',
      });
    },
  });

  // Test configuration mutation
  const testConfig = useMutation({
    mutationFn: async (testData: { email?: string; phone?: string }) => {
      return apiRequest({
        url: '/api/meta-conversions/test',
        method: 'POST',
        data: testData,
      });
    },
    onSuccess: () => {
      toast({
        title: 'Test Event Sent',
        description: 'Test event was successfully sent to Meta Conversions API.',
        variant: 'default',
      });
      setIsTesting(false);
    },
    onError: (error) => {
      toast({
        title: 'Test Failed',
        description: `Failed to send test event: ${error.toString()}`,
        variant: 'destructive',
      });
      setIsTesting(false);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveConfig.mutate(formData);
  };

  const handleTest = () => {
    setIsTesting(true);
    if (!testEmail && !testPhone) {
      toast({
        title: 'Test Data Required',
        description: 'Please provide either an email or phone number for testing.',
        variant: 'destructive',
      });
      setIsTesting(false);
      return;
    }

    const testData: { email?: string; phone?: string } = {};
    if (testEmail) testData.email = testEmail;
    if (testPhone) testData.phone = testPhone;

    testConfig.mutate(testData);
  };

  return (
    <div className="container mx-auto py-6">
      <Helmet>
        <title>Meta Conversions API Settings | Solar CRM</title>
      </Helmet>

      <div className="mb-8">
        <h1 className="text-3xl font-bold">Meta Conversions API Settings</h1>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          Configure the integration with Meta's Conversions API to track lead events
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>
            Failed to load configuration. Please try refreshing the page.
          </AlertDescription>
        </Alert>
      )}

      <Tabs defaultValue="settings" className="space-y-4">
        <TabsList>
          <TabsTrigger value="settings">
            <Cog className="h-4 w-4 mr-2" />
            Settings
          </TabsTrigger>
          <TabsTrigger value="testing">
            <ShieldCheck className="h-4 w-4 mr-2" />
            Testing
          </TabsTrigger>
          <TabsTrigger value="help">
            <InfoIcon className="h-4 w-4 mr-2" />
            Help
          </TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Meta Conversions API Configuration</CardTitle>
              <CardDescription>
                Enter your Meta Pixel ID and access token to enable lead event tracking
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="pixel_id">Meta Pixel ID</Label>
                    <Input
                      id="pixel_id"
                      name="pixel_id"
                      placeholder="123456789012345"
                      value={formData.pixel_id}
                      onChange={handleChange}
                      required
                    />
                    <p className="text-sm text-gray-500">
                      Your Meta Pixel ID can be found in Meta Business Manager
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="access_token">Access Token</Label>
                    <Input
                      id="access_token"
                      name="access_token"
                      type="password"
                      placeholder={config?.access_token ? '••••••••' : 'Enter your access token'}
                      value={formData.access_token}
                      onChange={handleChange}
                      required={!config?.access_token}
                    />
                    <p className="text-sm text-gray-500">
                      Generate a token with <code>ads_management</code> permission in Meta Business
                      Manager
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="test_event_code">Test Event Code (Optional)</Label>
                    <Input
                      id="test_event_code"
                      name="test_event_code"
                      placeholder="TEST12345"
                      value={formData.test_event_code}
                      onChange={handleChange}
                    />
                    <p className="text-sm text-gray-500">
                      Only needed during testing when using Test Events Tool in Meta Business
                      Manager
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="lead_event_source">CRM Name</Label>
                    <Input
                      id="lead_event_source"
                      name="lead_event_source"
                      placeholder="Solar CRM"
                      value={formData.lead_event_source}
                      onChange={handleChange}
                    />
                    <p className="text-sm text-gray-500">
                      This will appear as the source of events in Meta
                    </p>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="api_version">API Version</Label>
                    <Input
                      id="api_version"
                      name="api_version"
                      placeholder="v17.0"
                      value={formData.api_version}
                      onChange={handleChange}
                    />
                    <p className="text-sm text-gray-500">
                      Meta API version to use (default: v17.0)
                    </p>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={saveConfig.isPending || isLoading}
                >
                  {saveConfig.isPending ? 'Saving...' : 'Save Configuration'}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="testing">
          <Card>
            <CardHeader>
              <CardTitle>Test Meta Conversions API</CardTitle>
              <CardDescription>Send a test event to verify your configuration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <Alert>
                  <InfoIcon className="h-4 w-4" />
                  <AlertTitle>Information</AlertTitle>
                  <AlertDescription>
                    Enter an email or phone number to send a test event. This will help you verify
                    your Meta Conversions API integration is working correctly.
                  </AlertDescription>
                </Alert>

                <div className="grid gap-2">
                  <Label htmlFor="testEmail">Test Email</Label>
                  <Input
                    id="testEmail"
                    placeholder="test@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="testPhone">Test Phone</Label>
                  <Input
                    id="testPhone"
                    placeholder="+447700900000"
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                  />
                </div>

                <Button
                  onClick={handleTest}
                  disabled={isTesting || testConfig.isPending || (!testEmail && !testPhone)}
                  className="w-full"
                >
                  {testConfig.isPending ? 'Sending Test Event...' : 'Send Test Event'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="help">
          <Card>
            <CardHeader>
              <CardTitle>Meta Conversions API Help</CardTitle>
              <CardDescription>Learn how to set up and use Meta's Conversions API</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">What is Meta Conversions API?</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Meta's Conversions API allows your CRM to directly send lead events to Meta's
                    advertising platform. This helps improve ad performance and attribution,
                    especially when browser-based tracking is limited.
                  </p>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">Setup Instructions</h3>
                  <ol className="list-decimal pl-5 space-y-2 text-gray-600 dark:text-gray-400">
                    <li>
                      Go to Meta Business Manager at{' '}
                      <a
                        href="https://business.facebook.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        business.facebook.com
                      </a>
                    </li>
                    <li>Navigate to Events Manager and locate your Pixel ID</li>
                    <li>
                      Create an access token with <code>ads_management</code> permission
                    </li>
                    <li>Enter both values in the Settings tab</li>
                    <li>Use the Testing tab to verify the integration works</li>
                  </ol>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-2">When do events get sent?</h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    Events are automatically sent when:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-gray-600 dark:text-gray-400">
                    <li>A customer moves between pipeline stages</li>
                    <li>A new lead is created in the CRM</li>
                    <li>A customer signs a contract</li>
                  </ul>
                </div>

                <div className="pt-2">
                  <a
                    href="https://developers.facebook.com/docs/marketing-api/conversions-api/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 dark:text-blue-400 hover:underline flex items-center"
                  >
                    <InfoIcon className="h-4 w-4 mr-1" />
                    Meta Conversions API Documentation
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default MetaConversionsSettings;
